/**
 * Autostacker5DDemo — Main demo orchestrator for the 5D AutoStacker sales demo.
 *
 * Renders as an overlay on the ramp view. No routing changes needed —
 * just drop this into RampPage and it overlays everything.
 *
 * Exposes two toolbar items via children-like injection:
 *  - "Sync FlightBridge" button (renders in ActionIsland via prop callback)
 *  - "Reset Demo" mini button (renders in ActionIsland via prop callback)
 *
 * The sweep path renderer integrates into the MapboxScene Three.js scene via sceneRef.
 */

import { useEffect, useState, useCallback } from 'react'
import { RefreshCw, Wifi } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { FlightBridgeSyncOverlay } from './components/FlightBridgeSyncOverlay'
import { ArrivalNotificationModal } from './components/ArrivalNotificationModal'
import { AutostackerSidebar } from './components/AutostackerSidebar'
import { SweepPathRenderer } from './services/SweepPathRenderer'
import { useDemoMode } from './hooks/useDemoMode'
import { useTowAnimation } from './hooks/useTowAnimation'
import type * as THREE from 'three'
import type { MapboxCustomLayerService } from '@/services/3d/MapboxCustomLayerService'
import type { TowStep } from './data/towingPlan'

interface Autostacker5DDemoProps {
  /** The Three.js scene from MapboxScene — needed to render sweep paths */
  scene: THREE.Scene | null
  /** Layer service — needed so SweepPathRenderer can trigger map repaint */
  layerService: MapboxCustomLayerService | null
  /** All demo state from useDemoMode (called in parent RampPage) */
  demo: ReturnType<typeof useDemoMode>
  /** Called when user clicks "Place Manually" in the arrival modal */
  onPlaceManually: () => void
}

export function Autostacker5DDemo({ scene, layerService, demo, onPlaceManually }: Autostacker5DDemoProps) {
  // useState (not useRef) so that the renderer value propagates to useTowAnimation on re-render
  const [sweepRenderer, setSweepRenderer] = useState<SweepPathRenderer | null>(null)

  const {
    demoState,
    activeTowStep,
    completedStepIds,
    dismissArrival,
    startManualPlacement,
    startAutoFit,
    finishPlanning,
    selectTowStep,
    completeStep,
    resetDemo,
  } = demo

  // Create/destroy sweep path renderer when scene + layerService are available
  useEffect(() => {
    if (!scene || !layerService) return
    const renderer = new SweepPathRenderer(scene, layerService)
    setSweepRenderer(renderer)
    return () => {
      renderer.dispose()
      setSweepRenderer(null)
    }
  }, [scene, layerService])

  const handleStepComplete = useCallback((stepId: string) => {
    completeStep(stepId)
  }, [completeStep])

  const { animateStep, cancelAnimation } = useTowAnimation({
    sweepPathRenderer: sweepRenderer,
    scene,
    onComplete: handleStepComplete,
  })

  // When a tow step is selected, start the animation
  useEffect(() => {
    if (demoState === 'animating' && activeTowStep) {
      animateStep(activeTowStep)
    }
  }, [demoState, activeTowStep, animateStep])

  // Cleanup animation if demo resets
  useEffect(() => {
    if (demoState === 'idle' || demoState === 'resetting') {
      cancelAnimation()
    }
  }, [demoState, cancelAnimation])

  const handleManualPlace = () => {
    startManualPlacement()
    onPlaceManually()
  }

  const handleSelectStep = (step: TowStep) => {
    selectTowStep(step)
  }

  return (
    <>
      {/* Sync progress overlay — appears left of ActionIsland while syncing */}
      <FlightBridgeSyncOverlay visible={demoState === 'syncing'} />

      {/* Arrival modal */}
      <ArrivalNotificationModal
        open={demoState === 'arrival_modal'}
        onDismiss={dismissArrival}
        onPlaceManually={handleManualPlace}
        onAutoFit={startAutoFit}
      />

      {/* Autostacker sidebar — planning progress + tow plan */}
      <AutostackerSidebar
        demoState={demoState}
        completedStepIds={completedStepIds}
        onPlanningComplete={finishPlanning}
        onSelectStep={handleSelectStep}
        onClose={resetDemo}
      />
    </>
  )
}

// ─── Toolbar buttons rendered inside ActionIsland ──────────────────────────────

interface FlightBridgeSyncButtonProps {
  onClick: () => void
  isSyncing: boolean
}

export function FlightBridgeSyncButton({ onClick, isSyncing }: FlightBridgeSyncButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'w-10 h-10 rounded-xl transition-all relative',
            isSyncing && 'text-blue-500'
          )}
          onClick={onClick}
          disabled={isSyncing}
        >
          <Wifi className={cn('w-5 h-5', isSyncing && 'animate-pulse')} />
          {isSyncing && (
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-blue-500 animate-ping" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">
        <p>Sync FlightBridge</p>
      </TooltipContent>
    </Tooltip>
  )
}

interface DemoResetButtonProps {
  onClick: () => void
  isResetting: boolean
}

export function DemoResetButton({ onClick, isResetting }: DemoResetButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="w-7 h-7 rounded-lg opacity-30 hover:opacity-100 transition-opacity"
          onClick={onClick}
          disabled={isResetting}
        >
          <RefreshCw className={cn('w-3.5 h-3.5', isResetting && 'animate-spin')} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">
        <p className="text-xs">Reset Demo</p>
      </TooltipContent>
    </Tooltip>
  )
}
